import type { MeasurementPrefUtility } from '../helpers/MeasurementPref';
import { CLIMBING_GRADIENT_PERCENT } from './ascent';
import {
  paceUnit,
  paceValue,
  smallUnitValue,
  strideValue,
  verticalSpeedUnit,
  verticalSpeedValue,
  wholeNumber,
  withUnit,
} from './format';
import type { ActivitySeries, ActivityTab, RecordingActivity } from './recordingActivity';
import { valueAt } from './timeline';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** A cadence this far either side of the average on the steepest climb is worth a mention. */
export const CADENCE_CHANGE_SPM = 3;

export interface ActivityStat {
  icon: string;
  value: string;
  unit: string;
}

/** The numbers the map panel shows beside its tabs, as "136 avg". */
export function activityStats(
  tab: ActivityTab,
  activity: RecordingActivity,
  pref: MeasurementPrefUtility,
  t: Translate,
  locale?: string
): ActivityStat[] {
  const height = (meters: number) => smallUnitValue(meters, pref, locale);
  const metres = pref.meterSymbol();
  const gain = (meters: number) => ({ icon: 'trending_up', value: height(meters), unit: t('recordings.activity.unit_gain', { unit: metres }) });
  const loss = (meters: number) => ({ icon: 'trending_down', value: height(meters), unit: t('recordings.activity.unit_loss', { unit: metres }) });
  switch (tab) {
    case 'elevation': {
      const { gainMeters, lossMeters, highestMeters } = activity.elevation!;
      return [
        gain(gainMeters),
        loss(lossMeters),
        { icon: 'landscape', value: height(highestMeters), unit: t('recordings.activity.unit_max', { unit: metres }) },
      ];
    }
    case 'ascent': {
      const { climbing, descending, maxGradient } = activity.ascent!;
      const gradient = { icon: 'show_chart', value: String(Math.round(maxGradient)), unit: t('recordings.activity.unit_max_gradient', { unit: '%' }) };
      if (!climbing) return [gradient, gain(activity.elevation!.gainMeters), loss(activity.elevation!.lossMeters)];
      const unit = t('recordings.activity.unit_average', { unit: verticalSpeedUnit(pref) });
      return [
        { icon: 'trending_up', value: verticalSpeedValue(climbing.metersPerHour, pref, locale), unit },
        ...(descending ? [{ icon: 'trending_down', value: verticalSpeedValue(descending.metersPerHour, pref, locale), unit }] : []),
        gradient,
      ];
    }
    case 'heartRate': {
      const { average, max, min } = activity.heartRate!.summary;
      return [
        { icon: 'favorite', value: String(average), unit: t('recordings.activity.stat_avg') },
        { icon: 'trending_up', value: String(max), unit: t('recordings.activity.stat_max') },
        { icon: 'trending_down', value: String(min), unit: t('recordings.activity.stat_min_unit', { unit: t('recordings.activity.bpm') }) },
      ];
    }
    case 'steps': {
      const { totalSteps, averageStepsPerMinute, averageStrideMeters } = activity.steps!;
      return [
        { icon: 'directions_walk', value: wholeNumber(totalSteps, locale), unit: t('recordings.activity.steps_unit') },
        { icon: 'timer', value: wholeNumber(averageStepsPerMinute, locale), unit: t('recordings.activity.unit_average', { unit: t('recordings.activity.spm') }) },
        { icon: 'straighten', value: strideValue(averageStrideMeters, pref, locale), unit: t('recordings.activity.unit_stride', { unit: metres }) },
      ];
    }
    case 'pace': {
      const { movingSecondsPerMeter, fastestSecondsPerMeter } = activity.pace!;
      const unit = paceUnit(pref);
      return [
        { icon: 'timer', value: paceValue(movingSecondsPerMeter, pref), unit: t('recordings.activity.unit_moving', { unit }) },
        { icon: 'bolt', value: paceValue(fastestSecondsPerMeter, pref), unit: t('recordings.activity.unit_fastest', { unit }) },
      ];
    }
  }
}

export interface LegendSwatch {
  label: string;
  value: string;
  color: string;
}

/** The two ends of a graph's colour scale, as "Low 97 spm"; pace reads Fast and Slow. */
export function scaleLegend(tab: ActivityTab, series: ActivitySeries | null, t: Translate): LegendSwatch[] {
  if (!series || !('low' in series.colors)) return [];
  const { low, high, lowColor, highColor } = series.colors;
  const value = (y: number) => withUnit(series.formatValue(y), series.unit);
  const [lowLabel, highLabel] =
    tab === 'pace'
      ? [t('recordings.activity.legend_fast'), t('recordings.activity.legend_slow')]
      : [t('recordings.activity.legend_low'), t('recordings.activity.legend_high')];
  return [
    { label: lowLabel, value: value(low), color: lowColor },
    { label: highLabel, value: value(high), color: highColor },
  ];
}

export interface CadenceInsight {
  drops: boolean;
  cadence: string;
}

/** How cadence moved on the steepest climb; null without steps, a climb, or a change worth a mention. */
export function cadenceInsight(activity: RecordingActivity, locale?: string): CadenceInsight | null {
  const { steps, ascent } = activity;
  if (!steps || !ascent || ascent.samples.length === 0 || steps.samples.length === 0) return null;
  const steepest = ascent.samples.reduce((best, sample) => (sample.gradient > best.gradient ? sample : best));
  if (steepest.gradient < CLIMBING_GRADIENT_PERCENT) return null;
  const cadence = valueAt(steepest.distance, steps.samples.map((s) => s.distance), steps.samples.map((s) => s.value));
  const change = cadence - steps.averageStepsPerMinute;
  if (Math.abs(change) < CADENCE_CHANGE_SPM) return null;
  return { drops: change < 0, cadence: wholeNumber(cadence, locale) };
}
